'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { LayoutDashboard, MessageSquare, List, Folder, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { buildRouteWithAccount } from '@/lib/routes';
import { useAuth } from '@/contexts/auth-context';
import { useGmailAccount } from '@/contexts/gmail-account-context';
import { isUserPaid, isTrialExpired } from '@/lib/utils/subscription';
import { Logo } from '@/components/logo';

const navItems = [
  { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/messages', label: 'Messages', icon: MessageSquare },
  { href: '/rules', label: 'Rules', icon: List },
  { href: '/folders', label: 'Folders', icon: Folder },
  { href: '/settings', label: 'Settings', icon: Settings },
] as const;

/**
 * Persistent left-rail navigation for the logged-in app (Linear-style).
 *
 * Calm, dense vertical nav: the active item gets a subtle accent fill + accent text, the rest
 * are muted with a hover fill. Preserves the existing behavior from the header nav — routes are
 * account-scoped via buildRouteWithAccount, and non-Dashboard items are disabled for unpaid
 * users with the same tooltip. Rendered on md+ screens; mobile keeps the header's menu.
 */
export function DashboardSidebar() {
  const pathname = usePathname();
  const { user, loading: authLoading } = useAuth();
  const { selectedGmailAccountId } = useGmailAccount();

  const isPaid = user && !authLoading ? isUserPaid(user) : false;
  const restrictedTooltip =
    user && isTrialExpired(user)
      ? 'Your free trial has ended. Please add a payment method to access this feature.'
      : 'Subscription required. Start a free trial to access this feature.';

  return (
    <aside className="hidden w-56 shrink-0 flex-col border-r border-border md:flex">
      <div className="flex h-14 items-center px-4">
        <Logo href="/dashboard" />
      </div>
      <nav aria-label="Primary" className="flex flex-1 flex-col gap-0.5 px-2 py-2">
        {navItems.map(({ href, label, icon: Icon }) => {
          const isActive = pathname?.startsWith(href);
          const isRestricted = href !== '/dashboard' && !isPaid;
          const hrefWithAccount = buildRouteWithAccount(href, selectedGmailAccountId);

          if (isRestricted) {
            return (
              <div
                key={href}
                className="flex cursor-not-allowed items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-muted-foreground/60"
                title={restrictedTooltip}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{label}</span>
              </div>
            );
          }

          return (
            <Link
              key={href}
              href={hrefWithAccount}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary/10 text-foreground'
                  : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span>{label}</span>
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}
